import { useCallback } from "react";
import { QUEST_REWARD_TYPE } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { useHubUser } from "./user/useHubUser";
import { useApi } from "@/hooks/core/useApi";
import { useSpinningWheelTickets } from "@/hooks/spinningWheel";
import { useQueryClient } from "@tanstack/react-query";

interface UseTicketsOptions {
  enabled?: boolean;
}

/**
 * Hook to get user tickets (spinning wheel only)
 * Lottery feature has been removed - only spinning wheel tickets are used
 *
 * @param options - Configuration options
 * @param options.enabled - Whether to fetch tickets (default: false to avoid unnecessary queries)
 */
export const useTickets = (options?: UseTicketsOptions) => {
  const enabled = options?.enabled ?? false;
  const spinningWheelData = useSpinningWheelTickets({ enabled });

  return {
    tickets: spinningWheelData.tickets,
    isLoading: spinningWheelData.isLoading,
    ticketType: QUEST_REWARD_TYPE.SPINNING_WHEEL_TICKET,
  };
};

/**
 * Hook to poll for ticket count updates after claiming rewards or redeeming vouchers
 * Polls spinning wheel tickets endpoint and updates the cache when count increases
 */
export const usePollTicketUpdate = () => {
  const { auth, hubUser } = useHubUser();
  const apiHelper = useApi();
  const queryClient = useQueryClient();

  /**
   * Poll for ticket count update to ensure immediate UI feedback
   * API responses are automatically validated by apiHelper using Zod schemas
   */
  const pollForTicketUpdate = useCallback(
    async (currentTicketCount: number, maxAttempts = 20, delay = 500): Promise<void> => {
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        await new Promise((resolve) => setTimeout(resolve, delay));

        try {
          // Fetch spinning wheel tickets - automatically validated by apiHelper
          const freshTicketsData = await apiHelper.getWithAuth(auth, API_ENDPOINT.SPINNING_WHEEL_TICKETS, {});
          const newTicketCount = freshTicketsData.totalTickets;

          // Check if ticket count has increased
          if (hubUser?.id && newTicketCount && newTicketCount > currentTicketCount) {
            // Update the cache with validated data
            queryClient.setQueryData([QueryKeys.SPINNING_WHEEL_TICKETS, hubUser?.id], freshTicketsData);
            return; // Success - ticket count updated
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.warn(`Attempt ${attempt + 1} failed to fetch tickets:`, error);
        }
      }

      // If we get here, polling timed out - still invalidate queries as fallback
      // eslint-disable-next-line no-console
      console.warn("Ticket count polling timed out, falling back to query invalidation");
    },
    [auth, hubUser?.id, apiHelper, queryClient],
  );

  return {
    pollForTicketUpdate,
  };
};
